import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { computed, unref } from "vue";
import { format } from "date-fns";
import http from "@/helper/http";
import api from "@/helper/api";
import { RESOURCE_CACHE, queryKeys } from "@/config/queryConfig";
import { useBackgroundTasks } from "@/composables/useBackgroundTasks";

// ==================== STORE TRANSACTIONS ====================

export function useStoreTransactions(storeId, filters = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.ebmTransactions(storeId, filters),
    queryFn: async () => {
      // Use unref to handle both refs and primitive values
      const actualStoreId = unref(storeId);
      const actualFilters = unref(filters);

      // Only proceed if we have a valid store ID
      if (!actualStoreId) {
        throw new Error("Store ID is required");
      }

      const params = {
        page: actualFilters.page || 1,
        per_page: actualFilters.per_page || actualFilters.limit || 10,
        ...actualFilters,
      };

      const response = await http.get(api.store_transactions(actualStoreId), {
        params,
      });
      return response.data;
    },
    enabled: computed(() => {
      const actualStoreId = unref(storeId);
      return !!actualStoreId;
    }),
    staleTime: 30 * 1000, // 30 seconds - prevent rapid refetches
    ...RESOURCE_CACHE.ebmTransactions,
    ...options,
  });
}

export function useStoreTransactionDetails(uuid, options = {}) {
  return useQuery({
    queryKey: queryKeys.ebmTransactionDetails(uuid),
    queryFn: async () => {
      // Use unref to handle both refs and primitive values
      const actualUuid = unref(uuid);
      const response = await http.get(
        api.store_transaction_details(actualUuid)
      );
      return response.data;
    },
    enabled: computed(() => {
      const actualUuid = unref(uuid);
      return !!actualUuid;
    }),
    ...RESOURCE_CACHE.ebmTransactionDetails,
    ...options,
  });
}

export function useStoreTransactionStats(
  storeId,
  dateRange = null,
  options = {}
) {
  return useQuery({
    queryKey: queryKeys.ebmTransactionStats(storeId, dateRange),
    queryFn: async () => {
      // Use unref to handle both refs and primitive values
      const actualStoreId = unref(storeId);
      const actualDateRange = unref(dateRange);

      // Only proceed if we have a valid store ID
      if (!actualStoreId) {
        throw new Error("Store ID is required");
      }

      // Build query parameters
      const params = new URLSearchParams();

      // Add date range parameters if provided
      if (actualDateRange && actualDateRange.start) {
        // Set time to 00:00:00 for start date
        const startDate = new Date(actualDateRange.start);
        startDate.setHours(0, 0, 0, 0);

        params.append("start_time", format(startDate, "dd/MM/yyyy HH:mm:ss"));
      }
      if (actualDateRange && actualDateRange.end) {
        // Set time to 23:59:59 for end date
        const endDate = new Date(actualDateRange.end);
        endDate.setHours(23, 59, 59, 999);

        params.append("end_time", format(endDate, "dd/MM/yyyy HH:mm:ss"));
      }

      const queryString = params.toString();
      const url = queryString
        ? `${api.store_transactions_stats(actualStoreId)}?${queryString}`
        : api.store_transactions_stats(actualStoreId);

      const response = await http.get(url);
      return response.data;
    },
    enabled: computed(() => {
      const actualStoreId = unref(storeId);
      return !!actualStoreId;
    }),
    staleTime: 60 * 1000, // 1 minute - stats change less frequently
    ...RESOURCE_CACHE.ebmTransactionStats,
    ...options,
  });
}

// ==================== STORE TRANSACTION MUTATIONS ====================

export function useRetryStoreTransaction() {
  const queryClient = useQueryClient();
  const { addTask } = useBackgroundTasks();

  return useMutation({
    mutationFn: async ({ uuid }) => {
      const response = await http.post(
        api.store_transaction_retry(uuid),
        {} // Send empty JSON object to satisfy any validation
      );
      return response.data;
    },
    onSuccess: (data, { uuid, storeId }) => {
      // Add task to background monitor
      if (data.task_id) {
        addTask(
          data.task_id,
          "single_retry",
          {
            transactionUuid: uuid,
            message: data.message || "Retrying transaction",
          },
          storeId
        );
      }

      // Invalidate related queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.ebmTransactions(storeId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ebmTransactionStats(storeId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ebmTransactionDetails(uuid),
      });
    },
  });
}

export function useRetryAllFailedStoreTransactions() {
  const queryClient = useQueryClient();
  const { addTask } = useBackgroundTasks();

  return useMutation({
    mutationFn: async (storeId) => {
      const response = await http.post(
        api.store_transactions_retry_all(storeId),
        {} // Send empty JSON object to satisfy any validation
      );
      return response.data;
    },
    onSuccess: (data, storeId) => {
      // Add task to background monitor
      if (data.task_id) {
        addTask(
          data.task_id,
          "retry_all",
          {
            storeId: storeId,
            message: data.message || "Retrying all failed transactions",
          },
          storeId
        );
      }

      // Invalidate related queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.ebmTransactions(storeId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ebmTransactionStats(storeId),
      });
    },
  });
}

export function useUpdateStoreTransactionRetriable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ uuid, isRetriable }) => {
      const response = await http.patch(api.store_transaction_retriable(uuid), {
        is_retriable: isRetriable,
      });
      return response.data;
    },
    onSuccess: (data, { uuid }) => {
      // Invalidate related queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.ebmTransactions(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.ebmTransactionDetails(uuid),
      });
    },
  });
}
